import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { AgendaTabsNew, AgendaList, EmptyState } from "@components";
import { createAgendasTabDataFromDefault } from "@utils/agenda";
import { defaultAgendaTabsData } from "@constants/agenda";

const AgendaTabs = ({
  isLoadingAgendas,
  setAgendaId,
  setIsDeleteModalOpen,
  setDuplicateModalOpen,
  onActiveTabChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("active");
  const agendas = useSelector((state) => state.agendas.agendas);
  const pastAgendas = useSelector((state) => state.agendas.pastAgendas);

  // Initialize tab from URL parameter or sessionStorage
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const storedTab = sessionStorage.getItem("lastActiveAgendaTab");

    if (tabParam && (tabParam === "active" || tabParam === "past")) {
      setActiveTab(tabParam);
      sessionStorage.setItem("lastActiveAgendaTab", tabParam);
    } else if (storedTab && (storedTab === "active" || storedTab === "past")) {
      setActiveTab(storedTab);
      setSearchParams((prev) => {
        prev.set("tab", storedTab);
        return prev;
      });
    } else {
      setActiveTab("active");
      sessionStorage.setItem("lastActiveAgendaTab", "active");
    }
  }, [searchParams, setSearchParams]);

  // Notify parent of activeTab changes
  useEffect(() => {
    if (onActiveTabChange) {
      onActiveTabChange(activeTab);
    }
  }, [activeTab, onActiveTabChange]);

  // Handle tab change and update both URL and storage
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    sessionStorage.setItem("lastActiveAgendaTab", newTab);
    setSearchParams((prev) => {
      prev.set("tab", newTab);
      return prev;
    });
    if (onActiveTabChange) {
      onActiveTabChange(newTab);
    }
  };

  const agendaTabsData = useMemo(() => {
    const agendasData = createAgendasTabDataFromDefault(
      defaultAgendaTabsData[0],
      {
        isLoadingAgendas,
        agendas,
        setAgendaDeleteModalOpen: setIsDeleteModalOpen,
      },
    );

    const pastAgendasData = createAgendasTabDataFromDefault(
      defaultAgendaTabsData[1],
      {
        agendas: pastAgendas,
      },
    );

    return [agendasData, pastAgendasData];
  }, [agendas, pastAgendas, isLoadingAgendas, setIsDeleteModalOpen]);

  const tabs = useMemo(() => {
    return [
      {
        id: "active",
        label: "Active",
        count: agendas?.length || 0,
        data: agendaTabsData[0],
      },
      {
        id: "past",
        label: "Past Agendas",
        count: pastAgendas?.length || 0,
        data: agendaTabsData[1],
      },
    ];
  }, [agendaTabsData, agendas, pastAgendas]);

  const currentTabData = tabs.find((tab) => tab.id === activeTab)?.data;

  return (
    <>
      {/* New tab header design */}
      <AgendaTabsNew
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={tabs}
      />

      {/* Tab content */}
        {currentTabData && (
          <AgendaList
            {...currentTabData.content}
            setAgendaId={setAgendaId}
            setAgendaDuplicateModalOpen={setDuplicateModalOpen}
            emptyStateComponent={
              <EmptyState
                {...currentTabData.emptyStateComponentProps}
                container="true"
                imgProps={{ boxSize: "50px" }}
                textProps={{
                  mt: "12px",
                  textAlign: "center",
                }}
              />
            }
          />
        )}
    </>
  );
};

export default AgendaTabs;
